import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const useInfoModals = (user) => {
  const [pendingModals, setPendingModals] = useState([]);
  const [currentModals, setCurrentModals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch pending modals for the current user
  const fetchPendingModals = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/info-modals/user/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPendingModals(response.data || []);
    } catch (error) {
      console.error('Error fetching pending modals:', error);
      setError('Failed to fetch info modals');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Mark a modal as viewed
  const markModalAsViewed = useCallback(async (modalId, lastSlideViewed = 0, completed = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/info-modals/user/${modalId}/view`,
        { 
          lastSlideViewed,
          completedModal: completed
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove the modal from pending modals
      setPendingModals(prev => prev.filter(modal => modal._id !== modalId));
    } catch (error) {
      console.error('Error marking modal as viewed:', error);
    }
  }, []);

  // Show the next batch of modals
  const showNextModals = useCallback(() => {
    if (pendingModals.length === 0) return false;

    // For now, show all pending modals at once
    // In the future, we could implement batching logic here
    setCurrentModals([...pendingModals]);
    return true;
  }, [pendingModals]);

  // Handle modal completion
  const handleModalComplete = useCallback((modalIndex, slideIndex, completed) => {
    if (currentModals[modalIndex]) {
      markModalAsViewed(currentModals[modalIndex]._id, slideIndex, completed);
    }
  }, [currentModals, markModalAsViewed]);

  // Close current modals
  const closeModals = useCallback(() => {
    setCurrentModals([]);
  }, []);

  // Initialize - fetch pending modals when user changes
  useEffect(() => {
    fetchPendingModals();
  }, [fetchPendingModals]);

  // Auto-show modals when they become available
  useEffect(() => {
    if (pendingModals.length > 0 && currentModals.length === 0) {
      // Small delay to allow app to load first
      const timer = setTimeout(() => {
        showNextModals();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [pendingModals, currentModals, showNextModals]);

  return {
    pendingModals,
    currentModals,
    loading,
    error,
    showNextModals,
    closeModals,
    handleModalComplete,
    refetch: fetchPendingModals
  };
};